import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface CreateSubcategoryModalProps {
  parentCategoryId: string;
  parentCategoryName: string;
  trigger: React.ReactNode;
}

export const CreateSubcategoryModal = ({
  parentCategoryId,
  parentCategoryName,
  trigger,
}: CreateSubcategoryModalProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createSubcategoryMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      // Generate slug from name
      const slug = data.name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      // Check if slug already exists
      const { data: existing } = await supabase
        .from('categories')
        .select('id')
        .eq('slug', slug)
        .maybeSingle();

      if (existing) {
        throw new Error('A category with this name already exists');
      }

      // Create the new Level 3 category
      const { data: newCategory, error } = await supabase
        .from('categories')
        .insert({
          name: data.name,
          description: data.description,
          slug,
          parent_category_id: parentCategoryId,
          level: 3,
          hierarchy_level: 3,
          color: '#228B22', // Default green color
          requires_moderation: false,
        })
        .select()
        .single();

      if (error) throw error;
      return newCategory;
    },
    onSuccess: (newCategory) => {
      toast({
        title: 'Subcategory created',
        description: `"${newCategory.name}" has been created successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories', parentCategoryId] });
      setOpen(false);
      setName('');
      setDescription('');
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create subcategory',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a name for the subcategory.',
        variant: 'destructive',
      });
      return;
    }
    createSubcategoryMutation.mutate({ name, description });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Subcategory</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Create a new subcategory under "{parentCategoryName}"
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., U8 USYS National League"
              required
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this subcategory..."
              rows={3}
              maxLength={500}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createSubcategoryMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createSubcategoryMutation.isPending || !name.trim()}
            >
              {createSubcategoryMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Subcategory
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
